/**
 * Built-in `chart` widget — thin shell around Apache ECharts.
 *
 * The widget itself owns no chart semantics: the user supplies an ECharts
 * `option` object (typed into a textarea or uploaded as JSON) and we hand it
 * straight to `echarts.setOption`. That option lives in `config.option` and
 * round-trips through layout save/load, so the chart is part of the saved
 * dashboard.
 *
 * ECharts is loaded from a CDN on demand to keep the dashboard bundle small.
 * On base-portable SITE builds it's served from the site's own bundle instead
 * (zero public-CDN egress) — see ../../vendor.
 */
import type { DashboardWidget, DashboardWidgetInstance, DashboardCtx } from '../../Widget';
import { vendorLibUrl } from '../../vendor';

export interface ChartConfig {
  /** Raw ECharts option — anything `echarts.setOption` accepts. */
  option?: unknown;
}

const ECHARTS_JS_URL = vendorLibUrl(
  'echarts-5.5.1.min.js',
  'https://cdn.jsdelivr.net/npm/echarts@5.5.1/dist/echarts.min.js',
);

let echartsLoader: Promise<any> | null = null;
function loadECharts(): Promise<any> {
  if ((window as any).echarts) return Promise.resolve((window as any).echarts);
  if (!echartsLoader) {
    echartsLoader = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = ECHARTS_JS_URL;
      s.async = true;
      s.onload = () => resolve((window as any).echarts);
      s.onerror = () => reject(new Error('ECharts load failed'));
      document.head.appendChild(s);
    });
  }
  return echartsLoader;
}

const DEFAULT_OPTION = {
  title: { text: 'Example', textStyle: { color: '#d8dee9' } },
  tooltip: {},
  xAxis: { type: 'category', data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
  yAxis: { type: 'value' },
  series: [{ type: 'bar', data: [12, 20, 18, 25, 22, 30, 28] }],
  backgroundColor: 'transparent',
};

class ChartInstance implements DashboardWidgetInstance {
  private host: HTMLElement;
  private config: ChartConfig;
  private chartEl: HTMLDivElement | null = null;
  private chart: any = null;
  private destroyed = false;
  private renderToken = 0;
  private ro: ResizeObserver | null = null;

  constructor(host: HTMLElement, config: ChartConfig, _ctx: DashboardCtx) {
    this.host = host;
    this.config = config;
    this.render();
  }

  private showMessage(text: string, danger = false): void {
    this.host.innerHTML = '';
    const div = document.createElement('div');
    div.style.cssText =
      'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;text-align:center;padding:8px;font-size:12px;line-height:1.4;' +
      (danger ? 'color:#f0a4a4;background:#1a0e0e' : 'color:#7d8a96');
    div.textContent = text;
    this.host.appendChild(div);
  }

  private teardownChart(): void {
    if (this.ro) {
      try { this.ro.disconnect(); } catch { /* ignore */ }
      this.ro = null;
    }
    if (this.chart) {
      try { this.chart.dispose(); } catch { /* ignore */ }
      this.chart = null;
    }
    this.chartEl = null;
  }

  private render(): void {
    this.teardownChart();
    this.host.innerHTML = '';
    const token = ++this.renderToken;

    if (!this.config.option) {
      this.showMessage('No chart option set — open Configure to paste or upload an ECharts option JSON.');
      return;
    }

    const div = document.createElement('div');
    div.style.cssText = 'position:absolute;inset:0;background:transparent';
    this.host.appendChild(div);
    this.chartEl = div;

    loadECharts()
      .then((echarts) => {
        if (this.destroyed || token !== this.renderToken) return;
        try {
          this.chart = echarts.init(div, 'dark', { renderer: 'canvas' });
          this.chart.setOption(this.config.option, true);
          this.ro = new ResizeObserver(() => {
            if (this.chart) {
              try { this.chart.resize(); } catch { /* ignore */ }
            }
          });
          this.ro.observe(div);
        } catch (e) {
          this.showMessage(`ECharts setOption failed: ${(e as Error).message}`, true);
        }
      })
      .catch((err) => {
        if (!this.destroyed && token === this.renderToken) {
          this.showMessage(`ECharts load failed: ${err.message}`, true);
        }
      });
  }

  renderConfigUI(host: HTMLElement, onChange: (cfg: unknown) => void): void {
    host.innerHTML = '';
    const form = document.createElement('div');
    form.style.cssText = 'display:flex;flex-direction:column;gap:6px;font-size:12px;min-width:280px';

    const label = document.createElement('div');
    label.textContent = 'ECharts option (JSON)';
    label.style.cssText = 'color:#9aa4ad';
    form.appendChild(label);

    const ta = document.createElement('textarea');
    ta.spellcheck = false;
    ta.style.cssText =
      'min-height:180px;font-family:ui-monospace,Menlo,monospace;font-size:11px;line-height:1.4;' +
      'background:#0c0f13;color:#d8dee9;border:1px solid #2a313a;border-radius:3px;padding:6px;resize:vertical';
    ta.value = this.config.option
      ? JSON.stringify(this.config.option, null, 2)
      : JSON.stringify(DEFAULT_OPTION, null, 2);
    form.appendChild(ta);

    const status = document.createElement('div');
    status.style.cssText = 'font-size:11px;min-height:14px;color:#7d8a96';
    form.appendChild(status);

    const setStatus = (text: string, danger = false): void => {
      status.textContent = text;
      status.style.color = danger ? '#f0a4a4' : '#7d8a96';
    };

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap';

    const fileBtn = document.createElement('button');
    fileBtn.type = 'button';
    fileBtn.textContent = 'Upload JSON…';
    fileBtn.style.cssText =
      'padding:3px 8px;font-size:11px;background:#14181d;color:#d8dee9;border:1px solid #2a313a;border-radius:3px;cursor:pointer';

    const fileIn = document.createElement('input');
    fileIn.type = 'file';
    fileIn.accept = 'application/json,.json';
    fileIn.style.display = 'none';
    fileBtn.addEventListener('click', () => fileIn.click());
    fileIn.addEventListener('change', async () => {
      const f = fileIn.files?.[0];
      if (!f) return;
      try {
        const text = await f.text();
        // Validate by parsing, but write the prettified text back to the textarea.
        const parsed = JSON.parse(text);
        ta.value = JSON.stringify(parsed, null, 2);
        setStatus(`Loaded ${f.name}. Click Apply to render.`);
      } catch (e) {
        setStatus(`Upload parse failed: ${(e as Error).message}`, true);
      } finally {
        fileIn.value = '';
      }
    });
    row.appendChild(fileBtn);
    row.appendChild(fileIn);

    const exampleBtn = document.createElement('button');
    exampleBtn.type = 'button';
    exampleBtn.textContent = 'Insert example';
    exampleBtn.style.cssText = fileBtn.style.cssText;
    exampleBtn.addEventListener('click', () => {
      ta.value = JSON.stringify(DEFAULT_OPTION, null, 2);
      setStatus('Example loaded. Click Apply to render.');
    });
    row.appendChild(exampleBtn);

    const applyBtn = document.createElement('button');
    applyBtn.type = 'button';
    applyBtn.textContent = 'Apply';
    applyBtn.style.cssText =
      'padding:4px 10px;font-size:11px;background:#2c5cff;color:#fff;border:0;border-radius:3px;cursor:pointer';
    row.appendChild(applyBtn);

    form.appendChild(row);
    host.appendChild(form);

    const emit = (): void => {
      const text = ta.value.trim();
      if (!text) {
        onChange({ option: undefined } satisfies ChartConfig);
        return;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        setStatus(`Invalid JSON: ${(e as Error).message}`, true);
        return;
      }
      setStatus('Applied.');
      onChange({ option: parsed } satisfies ChartConfig);
    };
    applyBtn.addEventListener('click', emit);
  }

  // Intentionally no getState / applyState: the chart option lives entirely in
  // `config`, which already round-trips through the layout JSON. If we also
  // exposed it as state, rebuildCell's pre-dispose state capture would clobber
  // a just-applied new config with the old instance's stale option.

  dispose(): void {
    this.destroyed = true;
    this.teardownChart();
    this.host.innerHTML = '';
  }
}

export const chartWidget: DashboardWidget = {
  id: 'chart',
  label: 'Chart',
  description: 'Apache ECharts — paste or upload an ECharts option JSON.',
  mount(host, config, ctx) {
    const cfg = (config && typeof config === 'object' ? (config as ChartConfig) : {}) as ChartConfig;
    return new ChartInstance(host, cfg, ctx);
  },
};
